import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AccountMembership,
  AccountMembershipDto,
  AccountMembershipRole,
  CreateAccountMembershipDto,
} from '@mkp/account-membership/models';
import { Account } from '@mkp/account/models';
import { CoreListEnvelop } from '@mkp/shared/models';
import { BaseHttpResource, QueryParams } from '@mkp/shared/data-access';
import { QueryOptions } from '@mkp/shared/util-pagination';
import { map, Observable, filter as rxFilter } from 'rxjs';
import { AccountMembershipMapper } from './account-membership.mapper';

interface PaginatedAccountMemberships {
  accountMemberships: AccountMembership[];
  _links: any;
  filter: string;
  totalCount: number;
}

interface CreateAccountMembershipPayload {
  accountId: string;
  userId: string;
}

interface UpdateReceiveNewApplicationEmailsPayload {
  accountMembershipId: string;
  receiveNewApplicationEmails: boolean;
  _version: string;
}

@Injectable({ providedIn: 'root' })
export class AccountMembershipResource extends BaseHttpResource<
  AccountMembershipDto<Account>,
  CoreListEnvelop<AccountMembershipDto<Account>>
> {
  private mapper = inject(AccountMembershipMapper);
  constructor() {
    super('account-membership');
  }

  /**
   * Creates a new account membership with admin role
   * and active state.
   */
  createAccountMembership({
    accountId,
    userId,
  }: CreateAccountMembershipPayload): Observable<AccountMembership> {
    const createAccountMembershipDto: CreateAccountMembershipDto = {
      accountId,
      userId,
      roles: [{ role: AccountMembershipRole.Admin }],
    };

    return this.http
      .post<AccountMembershipDto<Account>>(this.url, createAccountMembershipDto)
      .pipe(map((dto) => this.mapper.fromJson(dto)));
  }

  /**
   * Returns a list of account memberships and maps it to AccountMembership model.
   */
  public listAccountMemberships(
    query: QueryParams,
    context?: HttpContext
  ): Observable<AccountMembership[]> {
    const options = query || { limit: 25 };
    return this.getWithQuery({ params: options, context }).pipe(
      map(({ _embedded }) => _embedded),
      rxFilter(Boolean),
      map(({ results: membershipDto }) => membershipDto.map((dto) => this.mapper.fromJson(dto)))
    );
  }

  /**
   * Returns a list of account memberships and maps it to AccountMembership model
   * with pagination details included.
   */
  public listPaginatedAccountMemberships(
    query: QueryOptions,
    accountId?: string
  ): Observable<PaginatedAccountMemberships> {
    const options = query || { limit: 25 };
    return this.getWithQuery({ params: options, withAccountId: accountId }).pipe(
      map(({ _embedded, filter, _links, totalCount }) => ({
        accountMemberships: _embedded.results.map((dto) => this.mapper.fromJson(dto)),
        _links,
        filter,
        totalCount,
      }))
    );
  }

  public patchRoles({
    roles,
    accountMembershipId,
    _version,
  }: {
    roles: { role: AccountMembershipRole }[];
    accountMembershipId: string;
    _version: string;
  }): Observable<AccountMembership> {
    return this.update(accountMembershipId, { roles, _version }).pipe(
      map((accountMembershipDto) => this.mapper.fromJson(accountMembershipDto))
    );
  }
  public deleteAccountMembership(accountMembershipId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/${accountMembershipId}`);
  }

  public updateReceiveNewApplicationEmails({
    accountMembershipId,
    receiveNewApplicationEmails,
    _version,
  }: UpdateReceiveNewApplicationEmailsPayload): Observable<AccountMembership> {
    return this.update(accountMembershipId, { receiveNewApplicationEmails, _version }).pipe(
      map((accountMembershipDto) => this.mapper.fromJson(accountMembershipDto))
    );
  }
}
